/**
 * SecondarySideBar Component
 *
 * A generic, reusable collapsible sidebar panel that can be placed on either
 * side of the layout. Supports both controlled (isOpen/onToggle) and
 * uncontrolled (defaultOpen) usage.
 */

import { useState } from "react"
import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx"
import { cn } from "@/lib/utils"

/** Default expanded width in pixels. */
const SIDEBAR_WIDTH_DEFAULT = 220
/** Collapsed width in pixels (just enough for the toggle icon). */
const SIDEBAR_WIDTH_COLLAPSED = 48

/** Props for the SecondarySideBar component. */
interface SecondarySideBarProps {
  /** The content rendered inside the sidebar when expanded. */
  children: React.ReactNode
  /**
   * Optional content rendered below the toggle button when the sidebar is collapsed.
   * Use this to show icon-only representations of the expanded content.
   */
  collapsedContent?: React.ReactNode
  /**
   * Optional content pinned to the bottom of the collapsed sidebar.
   * Use this for action buttons like "New Conversation".
   */
  collapsedFooter?: React.ReactNode
  /** Expanded width in pixels. Defaults to 220. */
  width?: number
  /** Which side of the layout this sidebar sits on. Defaults to "left". */
  side?: "left" | "right"
  /** Controlled mode: whether the sidebar is expanded. */
  isOpen?: boolean
  /** Controlled mode: callback to toggle the sidebar. */
  onToggle?: () => void
  /** Uncontrolled mode: initial open state. Defaults to true. Ignored if isOpen is provided. */
  defaultOpen?: boolean
  /** Title to display at the top of the sidebar and for the tooltip*/
  sidebarTitle?: string
  /**
   * Optional custom icon rendered inside the collapsed "expand" button in place
   * of the default panel-open chevron. Use this to show a domain icon.
   * When omitted, side-appropriate panel-open chevron is used.
   */
  collapsedExpandIcon?: React.ReactNode
}

/**
 * A generic collapsible sidebar with open/close toggle that works on either side.
 *
 * Use controlled mode (isOpen + onToggle) when the parent needs to programmatically
 * open/close the sidebar. Use uncontrolled mode (defaultOpen) for simple cases where
 * only the user toggles it.
 *
 * @param props - Component props.
 * @returns The rendered sidebar element.
 */
export default function SecondarySideBar({
  children,
  collapsedContent,
  collapsedFooter,
  width = SIDEBAR_WIDTH_DEFAULT,
  side = "left",
  isOpen: controlledIsOpen,
  onToggle,
  defaultOpen = true,
  sidebarTitle,
  collapsedExpandIcon,
}: SecondarySideBarProps): React.JSX.Element {
  // Internal state for uncontrolled mode
  const [internalOpen, setInternalOpen] = useState<boolean>(defaultOpen)

  // Use controlled props if provided, otherwise fall back to internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalOpen
  const handleToggle = onToggle ?? (() => setInternalOpen(prev => !prev))

  const isLeft = side === "left"
  const borderClass = isLeft ? "border-r border-border" : "border-l border-border"
  const OpenIcon = isLeft ? PanelLeftOpen : PanelRightOpen
  const CloseIcon = isLeft ? PanelLeftClose : PanelRightClose
  const togglePosition = isLeft ? "flex-row" : "flex-row-reverse"

  return (
    <aside
      className={cn(
        "relative shrink-0 h-full grid overflow-hidden",
        "transition-[grid-template-columns] duration-200 ease-out",
        borderClass
      )}
      style={{
        gridTemplateColumns: isOpen ? `${width}px` : `${SIDEBAR_WIDTH_COLLAPSED}px`,
      }}
    >
      <div className="relative overflow-hidden min-w-0">
        <nav
          className={cn(
            "absolute inset-0 flex flex-col gap-2 min-h-0",
            "transition-[transform,opacity] duration-200 ease-out",
            isOpen
              ? "translate-x-0 opacity-100"
              : (isLeft ? "-translate-x-full opacity-0" : "translate-x-full opacity-0")
          )}
          style={{ width: `${width}px`, willChange: isOpen ? "auto" : "transform, opacity" }}
        >
          <div className={`flex ${togglePosition} justify-between items-center border-b p-1`}>
            {sidebarTitle && (
              <div>
                <h2 className="text-sm font-semibold text-muted-foreground">{sidebarTitle}</h2>
              </div>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleToggle}
                  aria-label="Collapse sidebar"
                  className={`size-7 ${isLeft && "ml-auto"}`}
                >
                  <CloseIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Collapse sidebar</TooltipContent>
            </Tooltip>
          </div>
          {children}
        </nav>

        <div
          className={cn(
            "absolute inset-0 flex flex-col items-center py-2 gap-2",
            "transition-opacity duration-150 ease-out",
            isOpen ? "opacity-0 pointer-events-none" : "opacity-100 delay-100"
          )}
          style={{ width: `${SIDEBAR_WIDTH_COLLAPSED}px` }}
        >
          <span className="border-b w-full flex flex-row justify-center py-1 -mt-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleToggle}
                  aria-label={`Expand ${sidebarTitle}`}
                  className="size-7 shrink-0"
                >
                  {collapsedExpandIcon ?? <OpenIcon className="size-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Expand {sidebarTitle}</TooltipContent>
            </Tooltip>
          </span>

          {collapsedContent && (
            <div className="flex flex-col items-center gap-1 w-full overflow-y-auto flex-1">
              {collapsedContent}
            </div>
          )}

          {collapsedFooter && (
            <div className="flex flex-col items-center w-full shrink-0 border-t border-border pt-2">
              {collapsedFooter}
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}
